import React, { useState, useRef, useEffect } from 'react';
import { noop } from 'utils/noop';
import { useClickAway } from 'react-use';
import { ChevronRightNew16 } from 'ui/icons/aurora/16px/ChevronRightNew16';
import {
  MenuItemWithSideMenu,
  MenuItemText,
  MenuItemContentWrapper,
  MenuItemTextWrapper,
  CountBadge,
  ChevronRightWrapper,
} from './Styled';
import { useMenuContext } from './context';

type NestedMenuItemProps = {
  /** Component that will be displayed in the menu item if label is not provided. */
  content?: React.ReactNode;
  /** if the menu has a count badge for selected children */
  hasCountBadge?: boolean;
  /** unique identified for the component */
  id: string;
  /** If the menu is an input menu */
  isInputMenu?: boolean;
  /** If the menu is a select menu */
  isSelectMenu?: boolean;
  /** Text that will be displayed in the menu item if content is not provided.
  Required if the menu has search filter, as the string representation of the content displayed */
  label?: string;
  /** options */
  options: any[];
  /** Callback function to call when there is a selection change */
  onSelectionChange?: (id: string) => void;
  /** Callback function to call when there is a string query change */
  onStringQueryChange?: (query?: string) => void;
  /** the ids for the selected items */
  selectedOptionIds: string[];
  /** the default text for the input box */
  stringQuery?: string;
};
export const NestedMenuItem = ({
  content,
  hasCountBadge,
  id,
  isInputMenu,
  isSelectMenu,
  label,
  options,
  onSelectionChange = noop,
  onStringQueryChange = noop,
  selectedOptionIds = [],
  stringQuery,
  ...otherProps
}: NestedMenuItemProps) => {
  let count;
  if (isInputMenu) {
    count = stringQuery ? 1 : 0;
  }

  const { Menu } = useMenuContext();
  const [isOpen, setIsOpen] = useState(false);
  const [numChildrenSelected, setNumChildrenSelected] = useState(count);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (isSelectMenu) {
      setNumChildrenSelected(selectedOptionIds?.length);
    }
  }, [selectedOptionIds]);

  useClickAway(wrapperRef, () => {
    setIsOpen(false);
  });

  const handleSelectionChange = (currentSelection: string) => {
    onSelectionChange(currentSelection);
  };

  const handleStringQueryChange = (stringQueryInput?: string) => {
    onStringQueryChange(stringQueryInput);
    setNumChildrenSelected(stringQueryInput ? 1 : 0);
    setIsOpen(false);
  };

  return (
    <MenuItemWithSideMenu data-test={`nested-menu-item-wrapper-${id}`} ref={wrapperRef}>
      <MenuItemContentWrapper
        data-test={`nested-menu-item-${id}`}
        isActive={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <MenuItemText isActive={isOpen}>
          {label ? (
            <MenuItemTextWrapper>
              <span>{label}</span>
              {hasCountBadge && <CountBadge>{numChildrenSelected}</CountBadge>}
            </MenuItemTextWrapper>
          ) : (
            content
          )}
          <ChevronRightWrapper>
            <ChevronRightNew16 />
          </ChevronRightWrapper>
        </MenuItemText>
      </MenuItemContentWrapper>
      <Menu
        options={options}
        isChild={true}
        id={`nested-menu-${id}`}
        handleSelectionChange={handleSelectionChange}
        handleStringQueryChange={handleStringQueryChange}
        defaultSelectedOptionIds={selectedOptionIds}
        isOpen={isOpen}
        isSelectMenu={isSelectMenu}
        isInputMenu={isInputMenu}
        stringQuery={stringQuery}
        {...otherProps}
      />
    </MenuItemWithSideMenu>
  );
};
